package org.teddapp.project.ui.focus

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.Stable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.tedd.db.Pomodoro_item
import com.tedd.db.Pomodoro_session
import com.tedd.db.Focus_session
import com.tedd.db.Skill
import com.tedd.db.Task
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.launch
import org.teddapp.project.core.CoreFacts
import org.teddapp.project.core.TeddIdentity
import org.teddapp.project.core.CoreSession
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.db.deviceLabel
import org.teddapp.project.ui.pomodoro.POM_TODAY_ISO
import org.teddapp.project.ui.pomodoro.PomPriority
import org.teddapp.project.ui.pomodoro.resolveItemTitle

/** Onglets du centre de l'écran Focus, communs à toutes les largeurs. */
internal enum class FocusTab { Today, History, Review }

/** Course terminée : la ligne écrite en base et l'élément travaillé, pour l'écran de fin. */
internal data class FinishedRun(val rowId: String, val run: TimerRun, val item: Pomodoro_item?, val skillId: String? = null)

/** Durée proposée quand la session n'en a pas encore. */
internal const val FOCUS_TIMER_DEFAULT_MIN = 25

/**
 * État et actions de l'écran Focus, indépendants de la mise en page.
 *
 * Les données (session, tâches, historique) sont alimentées par
 * [rememberFocusModel] ; les actions écrivent via les dépôts et affichent un
 * message éphémère. Les deux mises en page, compacte et étendue, lisent le même
 * objet : la logique n'existe qu'une fois.
 */
@Stable
internal class FocusModel(
    private val repos: TeddRepositories,
    private val scope: CoroutineScope,
) {
    // ── Données ────────────────────────────────────────────────────
    var session by mutableStateOf<Pomodoro_session?>(null)
    var currentSession by mutableStateOf<Pomodoro_session?>(null)
    var items by mutableStateOf<List<Pomodoro_item>>(emptyList())
    var sortedItems by mutableStateOf<List<Pomodoro_item>>(emptyList())
    var history by mutableStateOf<List<Pomodoro_session>>(emptyList())
    var skills by mutableStateOf<List<Skill>>(emptyList())
    var sessions by mutableStateOf<List<Focus_session>>(emptyList())
    /** Appareil distant qui fait tourner la course, ou null si c'est celui-ci. */
    var remoteTimerDevice by mutableStateOf<String?>(null)
    /** Session du noyau, quand la bibliotheque native est la. */
    var coreSession: CoreSession? = null
    /** Table tâche → compétence, dérivée des objectifs rattachés. */
    var skillOfTask by mutableStateOf<Map<String, String>>(emptyMap())

    /** Tâches qui sont des feuilles de parcours : une course dessus est attribuée à la feuille dans le noyau. */
    var parcoursLeafIds by mutableStateOf<Set<String>>(emptySet())
    var resolveMeta: (Pomodoro_item) -> ItemMeta by mutableStateOf({ item -> metaFromItem(item, resolveItemTitle(item, emptyMap(), emptyMap(), emptyMap(), emptyMap(), emptyMap())) })

    // ── Minuteur (hissé dans FocusTimerController) ─────────────────
    var timerMinutes by mutableStateOf(FOCUS_TIMER_DEFAULT_MIN)
    var timerActive by mutableStateOf(false)
    var timerPaused by mutableStateOf(false)
    var timerRemainingSec by mutableStateOf(0)
    var timerTotalSec by mutableStateOf(0)

    // ── État d'interface partagé ───────────────────────────────────
    var tab by mutableStateOf(FocusTab.Today)
    var selectedItemId by mutableStateOf<String?>(null)
    var quickDraft by mutableStateOf("")
    /** Temps qu'on se donne pour la tâche rapide en cours de saisie, en minutes ; vide = pas fixé. */
    var quickMinutesDraft by mutableStateOf("")
    var snackbar by mutableStateOf<String?>(null)
    /** Dernière course enregistrée, en attente d'une phrase de fin (facultative). */
    var finishedRun by mutableStateOf<FinishedRun?>(null)

    val doneCount: Int get() = items.count { it.completed != 0L }
    val totalCount: Int get() = items.size
    val progressPct: Int get() = if (totalCount == 0) 0 else (doneCount * 100) / totalCount
    /** Somme des temps prévus de la journée, toutes tâches confondues ; 0 quand aucune n'en porte. */
    val plannedMinutes: Int get() = items.sumOf { resolveMeta(it).minutes?.coerceAtLeast(0) ?: 0 }
    /** Part de ce temps déjà cochée. */
    val doneMinutes: Int get() = items.filter { it.completed != 0L }.sumOf { resolveMeta(it).minutes?.coerceAtLeast(0) ?: 0 }
    val canTerminate: Boolean get() = doneCount > 0 && currentSession?.status == "active"
    val hasExtension: Boolean get() = currentSession?.date_end != null
    val selectedItem: Pomodoro_item? get() = selectedItemId?.let { id -> items.firstOrNull { it.id == id } }
    val timerProgress: Float
        get() = if (timerActive && timerTotalSec > 0) 1f - timerRemainingSec.toFloat() / timerTotalSec.toFloat() else 0f

    // ── Actions ────────────────────────────────────────────────────
    /**
     * Compétence de la dernière course qui en portait une.
     *
     * Une course lancée sans choisir d'élément ne se rattachait à rien : quinze
     * courses de suite ont ainsi compté pour zéro compétence. Reprendre la dernière
     * connue est presque toujours juste — on travaille rarement une matière
     * différente d'une séance à l'autre — et l'écran de fin permet d'en changer.
     */
    val lastUsedSkillId: String?
        get() {
            // Archiver veut dire « pas maintenant ». Reprendre une compétence archivée
            // la rattacherait en silence, et le sélecteur de fin de course, qui écarte
            // les archivées, ne la montrerait même pas pour qu'on puisse corriger.
            val enJeu = skills.filter { it.archived == 0L }.map { it.id }.toHashSet()
            return sessions
                .filter { it.is_deleted == 0L && it.skill_id != null && it.skill_id in enJeu }
                .maxByOrNull { it.started_at }
                ?.skill_id
        }

    /** Nom de la compétence qui recevra la prochaine course libre, ou null. */
    fun pendingSkillName(): String? =
        lastUsedSkillId?.let { id -> skills.firstOrNull { it.id == id }?.name }

    /** Lance la course sur tous les appareils : la ligne partagée fait foi. */
    fun startTimer(itemId: String? = null) {
        if (itemId != null) selectedItemId = itemId
        val item = itemId?.let { id -> items.firstOrNull { it.id == id } }
        val skill = item?.takeIf { it.source_type != "quick" }?.source_id?.let { skillOfTask[it] }
            ?: lastUsedSkillId
        scope.launch { repos.timerState.start(timerMinutes, itemId, skill, deviceLabel) }
        coreSession?.record(
            userId = TeddIdentity.USER_ID,
            eventJson = CoreFacts.focusStarted(
                competenceId = skill ?: CoreFacts.UNCLASSIFIED,
                taskName = item?.let { resolveMeta(it).title } ?: "Course de focus",
            ),
        )
    }

    /**
     * Lance une course sur une micro ou une action de parcours : la tâche entre dans
     * le Pomodoro du jour si elle n'y est pas, la durée devient celle de la tâche, et
     * la course démarre, rattachée à la compétence de la tâche.
     */
    fun launchTask(task: Task, minutes: Int, skillId: String? = null) {
        val sourceType = if (task.parent_kind == "project") "project_task" else "action"
        scope.launch {
            val itemId = repos.pomodoro.addFromSource(POM_TODAY_ISO, sourceType, task.id)
                ?: items.firstOrNull { it.source_type == sourceType && it.source_id == task.id }?.id
            val m = minutes.coerceIn(1, 180)
            currentSession?.let { repos.pomodoroSessions.setTimerMinutes(it.id, m.toLong()) }
            timerMinutes = m
            selectedItemId = itemId
            val skill = skillId ?: skillOfTask[task.id] ?: lastUsedSkillId
            repos.timerState.start(m, itemId, skill, deviceLabel)
            coreSession?.record(
                userId = TeddIdentity.USER_ID,
                eventJson = CoreFacts.focusStarted(competenceId = skill ?: CoreFacts.UNCLASSIFIED, taskName = task.title),
            )
            tab = FocusTab.Today
            snackbar = "Course de $m min lancée"
        }
    }

    fun togglePause() {
        scope.launch {
            if (timerPaused) repos.timerState.resume() else repos.timerState.pause()
        }
    }

    /** Arrêt manuel : la course est enregistrée si elle a duré au moins une minute. */
    fun stopTimer() {
        val run = FocusTimerController.runOnManualStop()
        scope.launch { repos.timerState.stop() }
        if (run != null) onRunEnded(run)
    }

    fun toggleSelect(id: String) { selectedItemId = if (selectedItemId == id) null else id }

    fun submitQuick() {
        val t = quickDraft.trim()
        if (t.isEmpty()) return
        val minutes = quickMinutesDraft.trim().toLongOrNull()?.takeIf { it > 0 }
        scope.launch { repos.pomodoro.addQuick(POM_TODAY_ISO, t, estimatedMinutes = minutes); snackbar = "Tâche ajoutée" }
        quickDraft = ""
        quickMinutesDraft = ""
    }

    fun toggleItem(item: Pomodoro_item) { scope.launch { repos.pomodoro.toggleItem(item.id) } }
    fun setPriority(item: Pomodoro_item, p: PomPriority) { scope.launch { repos.pomodoroItems.setPriority(item.id, p.value) } }
    /**
     * Déplace une tâche de la liste du jour, par glisser. Elle reste dans son groupe :
     * trois tâches urgentes s'ordonnent entre elles, mais une urgente ne passe pas sous
     * une moyenne. Le rang manuel de toute la liste est réécrit dans l'ordre affiché.
     */
    fun moveItem(fromIndex: Int, toIndex: Int) {
        val reordered = moveWithinGroup(sortedItems, fromIndex, toIndex) { (it.completed != 0L) to it.priority } ?: return
        sortedItems = reordered
        scope.launch { repos.pomodoroItems.applyOrder(reordered.map { it.id }) }
    }

    fun removeItem(item: Pomodoro_item) {
        scope.launch { repos.pomodoroItems.softDelete(item.id) }
        if (selectedItemId == item.id) selectedItemId = null
    }

    /** Tâche rapide en cours de modification, ou null quand la boîte est fermée. */
    var editingQuick by mutableStateOf<Pomodoro_item?>(null)

    fun openQuickEdit(item: Pomodoro_item) { if (item.source_type == "quick") editingQuick = item }

    fun saveQuickEdit(title: String, minutes: Long?) {
        val item = editingQuick ?: return
        val t = title.trim()
        if (t.isEmpty()) return
        scope.launch { repos.pomodoroItems.updateQuick(item.id, t, minutes?.takeIf { it > 0 }); snackbar = "Tâche modifiée" }
        editingQuick = null
    }

    fun terminateToday() {
        scope.launch {
            repos.pomodoro.terminateToday(POM_TODAY_ISO)
            session = repos.pomodoro.openTodaySession(POM_TODAY_ISO)
            snackbar = "Journée terminée 🎉"
        }
    }

    fun setExtension(endDate: String?, message: String? = null) {
        scope.launch {
            repos.pomodoro.setExtension(POM_TODAY_ISO, endDate)
            session = repos.pomodoro.openTodaySession(POM_TODAY_ISO)
            if (message != null) snackbar = message
        }
    }

    fun cloneSession(sourceId: String) {
        scope.launch {
            val n = repos.pomodoro.cloneSession(POM_TODAY_ISO, sourceId)
            session = repos.pomodoro.openTodaySession(POM_TODAY_ISO)
            snackbar = if (n > 0) "$n tâche${if (n > 1) "s" else ""} reprise${if (n > 1) "s" else ""}" else "Tout est déjà dans la liste"
            tab = FocusTab.Today
        }
    }

    /** Enregistre la course dans focus_session et ouvre l'écran de fin. */
    fun onRunEnded(run: TimerRun) {
        val item = run.itemId?.let { id -> items.firstOrNull { it.id == id } }
        val taskId = item?.takeIf { it.source_type != "quick" }?.source_id
        scope.launch {
            // Compétence héritée de l'objectif de la tâche, si elle en a une.
            // Faute de tâche, on reprend la dernière compétence travaillée plutôt que
            // de laisser la course ne compter pour rien. Elle est visible et modifiable
            // sur l'écran de fin.
            val inherited = taskId?.let { id -> skillOfTask[id] } ?: lastUsedSkillId
            val rowId = repos.focusSessions.record(
                startedAt = run.startedAt, endedAt = run.endedAt, focusMinutes = run.minutes.toLong(),
                breakMinutes = 0L, pomodoros = if (run.completed) 1L else 0L, taskId = taskId,
                skillId = inherited,
            )
            // La tâche reçoit le temps réellement passé dessus. Sans ça, l'estimation
            // n'a jamais de contrepartie et rien ne peut mesurer l'écart.
            if (taskId != null && run.minutes > 0) {
                repos.tasks.addRealMinutes(taskId, run.minutes.toLong())
            }
            finishedRun = FinishedRun(rowId = rowId, run = run, item = item, skillId = inherited)
        }
    }

    /** Rattache la course terminée à une compétence (ou la détache). */
    fun tagRunSkill(skillId: String?) {
        val fr = finishedRun ?: return
        scope.launch { repos.skills.tagFocusSession(fr.rowId, skillId) }
        finishedRun = fr.copy(skillId = skillId)
    }

    fun saveRunNote(text: String) {
        val fr = finishedRun ?: return
        scope.launch { repos.focusSessions.setNote(fr.rowId, text.trim()) }
        recordFocusCompleted(fr, text.trim())
        finishedRun = null
        snackbar = "Course enregistrée"
    }

    /**
     * Écrit la course terminée dans le journal du noyau. Sans compétence rattachée, le
     * fait est écrit quand même sous « non classé » : le temps compte, et la question
     * socratique arrive.
     */
    private fun recordFocusCompleted(fr: FinishedRun, explanation: String) {
        val core = coreSession ?: return
        val title = fr.item?.let { resolveMeta(it).title } ?: "Course de focus"
        val leafId = fr.item?.takeIf { it.source_type != "quick" }?.source_id?.takeIf { it in parcoursLeafIds }
        core.record(
            userId = TeddIdentity.USER_ID,
            eventJson = CoreFacts.focusCompleted(
                competenceId = fr.skillId ?: CoreFacts.UNCLASSIFIED,
                taskName = title,
                seconds = fr.run.minutes * 60L,
                explanation = explanation.ifBlank { "Pas d'explication écrite." },
                feuilleId = leafId,
            ),
        )
    }


    /** Passer sans écrire : la course compte quand même, le fait est écrit sans phrase. */
    /** Pause active proposée après la course : ouverte à la demande. */
    var bdnfOpen by mutableStateOf(false)

    /** Enregistre la pause active : le noyau en tire la stimulation BDNF. */
    fun recordBdnf(exercise: String, seconds: Int, efficiency: Double) {
        if (seconds < 1) { bdnfOpen = false; return }
        coreSession?.record(TeddIdentity.USER_ID, CoreFacts.bdnfActivity(exercise, seconds, heartRate = 0, efficiency = efficiency))
        bdnfOpen = false
        snackbar = "Pause active enregistrée"
    }

    fun dismissRun() {
        finishedRun?.let { recordFocusCompleted(it, "") }
        finishedRun = null
    }

    fun relaunchRun() {
        val fr = finishedRun ?: return
        finishedRun = null
        startTimer(fr.item?.id)
    }

    fun pickTimerMinutes(m: Int) {
        val s = currentSession
        scope.launch {
            if (s != null) repos.pomodoroSessions.setTimerMinutes(s.id, m.toLong())
            repos.timerState.setPlannedMinutes(m)
        }
    }
}

/** Construit et alimente le [FocusModel] à partir des dépôts. */
@Composable
internal fun rememberFocusModel(repos: TeddRepositories): FocusModel {
    val scope = rememberCoroutineScope()
    val model = remember(repos) { FocusModel(repos, scope) }

    model.sessions = repos.focusSessions.observeAll().collectAsState(emptyList()).value
    // La session du jour se rechoisit dès que les sessions changent : un appareil neuf ouvre
    // une journée vide, puis reçoit du serveur la session étendue qui couvre ce jour ; c'est
    // elle qu'il faut montrer, pas la journée vide créée une minute plus tôt.
    val allSessions by repos.pomodoroSessions.observeAll().collectAsState(emptyList())
    val coveringToday = remember(allSessions) {
        allSessions.filter { it.is_deleted == 0L && it.date_start <= POM_TODAY_ISO && (it.date_end ?: it.date_start) >= POM_TODAY_ISO }
            .map { it.id }
    }
    LaunchedEffect(coveringToday) { model.session = repos.pomodoro.openTodaySession(POM_TODAY_ISO) }
    LaunchedEffect(model) { FocusTimerController.runs.collect { run -> model.onRunEnded(run) } }
    // Le chronomètre partagé : la ligne synchronisée pilote l'affichage, ici comme ailleurs.
    val timerRow by repos.timerState.observe().collectAsState(null)
    LaunchedEffect(timerRow) { FocusTimerController.adopt(timerRow, deviceLabel) }
    model.remoteTimerDevice = FocusTimerController.remoteDevice.collectAsState().value
    val sessionFlow = model.session?.id?.let { repos.pomodoroSessions.observeById(it) }
    val sessionLive by (sessionFlow ?: flowOf(null)).collectAsState(initial = model.session)
    model.currentSession = sessionLive ?: model.session

    val itemsFlow = model.currentSession?.id?.let { repos.pomodoroItems.observeBySession(it) } ?: flowOf(emptyList())
    val items by itemsFlow.collectAsState(emptyList())
    model.items = items
    model.sortedItems = remember(items) { sortItems(items) }
    model.history = repos.pomodoroSessions.observeHistory(POM_TODAY_ISO, 30L).collectAsState(emptyList()).value
    model.skills = repos.skills.observeAll().collectAsState(emptyList()).value

    val allTasks by repos.tasks.observeAll().collectAsState(emptyList())
    val allObjectives by repos.objectives.observeAll().collectAsState(emptyList())
    val allMilestones by repos.milestones.observeAll().collectAsState(emptyList())
    val allProjects by repos.projects.observeAll().collectAsState(emptyList())
    val allOrgs by repos.organizations.observeAll().collectAsState(emptyList())
    val tasksById = remember(allTasks) { allTasks.associateBy { it.id } }
    model.skillOfTask = remember(allTasks, allMilestones, allObjectives, allProjects) {
        val objBySkill = allObjectives.filter { it.skill_id != null }.associate { it.id to it.skill_id!! }
        val projBySkill = allProjects.filter { it.skill_id != null }.associate { it.id to it.skill_id!! }
        val msToObj = allMilestones.associate { it.id to it.objective_id }
        allTasks.mapNotNull { t ->
            val skill = when (t.parent_kind) {
                "milestone" -> t.parent_id?.let { msToObj[it] }?.let { objBySkill[it] }
                "project" -> t.parent_id?.let { projBySkill[it] }
                else -> t.parent_id?.let { objBySkill[it] }
            }
            skill?.let { t.id to it }
        }.toMap()
    }
    val allSteps by repos.parcours.observeAll().collectAsState(emptyList())
    val leafIds = remember(allTasks, allMilestones, allObjectives, allSteps) {
        val stepIds = allSteps.map { it.id }.toHashSet()
        val projectIds = allSteps.mapNotNull { it.project_id }.toHashSet()
        val objInStep = allObjectives.filter { it.step_id != null && it.step_id in stepIds }.map { it.id }.toHashSet()
        val msInStep = allMilestones.filter { it.objective_id in objInStep }.map { it.id }.toHashSet()
        allTasks.filter { t ->
            (t.parent_kind == "milestone" && t.parent_id in msInStep) || (t.parent_kind == "project" && t.parent_id in projectIds)
        }.map { it.id }.toHashSet()
    }
    model.parcoursLeafIds = leafIds
    val milestonesById = remember(allMilestones) { allMilestones.associateBy { it.id } }
    val objectivesById = remember(allObjectives) { allObjectives.associateBy { it.id } }
    val projectsById = remember(allProjects) { allProjects.associateBy { it.id } }
    val orgsById = remember(allOrgs) { allOrgs.associateBy { it.id } }
    model.resolveMeta = remember(tasksById, milestonesById, objectivesById, projectsById, orgsById) {
        { item -> metaFromItem(item, resolveItemTitle(item, tasksById, milestonesById, objectivesById, projectsById, orgsById)) }
    }

    model.timerMinutes = model.currentSession?.timer_minutes?.toInt() ?: FOCUS_TIMER_DEFAULT_MIN
    model.timerActive = FocusTimerController.active.collectAsState().value
    model.timerPaused = FocusTimerController.paused.collectAsState().value
    model.timerTotalSec = FocusTimerController.totalSec.collectAsState().value
    model.timerRemainingSec = FocusTimerController.remainingSec.collectAsState().value

    LaunchedEffect(model.snackbar) {
        if (model.snackbar != null) { delay(2400); model.snackbar = null }
    }
    return model
}

/**
 * Déplace l'élément [fromIndex] vers [toIndex] sans quitter son groupe : la cible est
 * ramenée aux bornes des éléments qui partagent sa clé. Null si rien ne change.
 */
internal fun <T> moveWithinGroup(list: List<T>, fromIndex: Int, toIndex: Int, groupOf: (T) -> Any): List<T>? {
    if (fromIndex !in list.indices || fromIndex == toIndex) return null
    val group = groupOf(list[fromIndex])
    val first = list.indexOfFirst { groupOf(it) == group }
    val last = list.indexOfLast { groupOf(it) == group }
    val target = toIndex.coerceIn(first, last)
    if (target == fromIndex) return null
    return list.toMutableList().apply { add(target, removeAt(fromIndex)) }
}

/** Vrai si la tâche [index] a une voisine de même groupe dans la direction donnée (-1 ou +1). */
internal fun canMoveWithinGroup(list: List<Pomodoro_item>, index: Int, direction: Int): Boolean {
    val other = index + direction
    if (index !in list.indices || other !in list.indices) return false
    fun group(it: Pomodoro_item) = (it.completed != 0L) to it.priority
    return group(list[index]) == group(list[other])
}

/** Non faites d'abord, puis priorité, puis ordre manuel. */
internal fun sortItems(items: List<Pomodoro_item>): List<Pomodoro_item> =
    items.sortedWith(
        compareBy<Pomodoro_item> { it.completed != 0L }
            .thenBy { it.priority }
            .thenBy { it.order_index }
            .thenBy { it.id },
    )
